/**
 * AutoElite API — Pure HTTP client for Spring Boot backend.
 * MongoDB is the ONLY data store. No localStorage data fallbacks.
 * localStorage is used ONLY for JWT tokens (ae_access_token, ae_refresh_token)
 * and user session profile (ae_user, ae_admin).
 */
import axios from 'axios';
import { useState, useEffect, useCallback } from 'react';

export const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api/v1';

/* ── Token storage ───────────────────────────────────────── */
const TK = { access: 'ae_access_token', refresh: 'ae_refresh_token' };

export const getAccessToken  = () => localStorage.getItem(TK.access);
export const getRefreshToken = () => localStorage.getItem(TK.refresh);
export const setTokens = (a, r) => {
  if (a) localStorage.setItem(TK.access,  a);
  if (r) localStorage.setItem(TK.refresh, r);
};
export const clearTokens = () => {
  localStorage.removeItem(TK.access);
  localStorage.removeItem(TK.refresh);
  // Also clear cached user sessions on token clear
  localStorage.removeItem('ae_user');
  localStorage.removeItem('ae_admin');
};
export const isAuthenticated = () => Boolean(getAccessToken());

/* ── Axios instance ──────────────────────────────────────── */
export const http = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 20000,
});

/* ── Request interceptor: attach JWT ── */
http.interceptors.request.use(cfg => {
  const token = getAccessToken();
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

/* ── Response interceptor: silent refresh on 401 (expired token) ── */
let isRefreshing = false;
let failedQueue  = [];
const processQueue = (err, token = null) => {
  failedQueue.forEach(p => err ? p.reject(err) : p.resolve(token));
  failedQueue = [];
};

http.interceptors.response.use(
  res => res,
  async err => {
    const orig = err.config;
    const status = err.response?.status;

    // Only attempt token refresh on 401 (Unauthorized = token expired/invalid)
    // Skip auth endpoints to avoid infinite loops
    if (status === 401 && !orig._retry && !orig.url?.includes('/auth/')) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => failedQueue.push({ resolve, reject }))
          .then(tok => {
            orig.headers.Authorization = `Bearer ${tok}`;
            return http(orig);
          });
      }

      orig._retry  = true;
      isRefreshing = true;
      const rt = getRefreshToken();

      if (!rt) {
        clearTokens();
        processQueue(err, null);
        isRefreshing = false;
        return Promise.reject(err);
      }

      try {
        const { data } = await axios.post(`${BASE_URL}/auth/refresh`, { refreshToken: rt });
        const at = data.data?.accessToken;
        setTokens(at, data.data?.refreshToken);
        processQueue(null, at);
        orig.headers.Authorization = `Bearer ${at}`;
        return http(orig);
      } catch (refreshErr) {
        processQueue(refreshErr, null);
        clearTokens();
        return Promise.reject(refreshErr);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(err);
  }
);

/* ── Auth ────────────────────────────────────────────────── */
export const registerUser = async body => {
  const { data } = await http.post('/auth/register', body);
  setTokens(data.data?.accessToken, data.data?.refreshToken);
  return data.data;
};

export const loginUser = async body => {
  const { data } = await http.post('/auth/login', body);
  setTokens(data.data?.accessToken, data.data?.refreshToken);
  return data.data;
};

export const logoutUser = () => clearTokens();

/* ── Public catalogue ────────────────────────────────────── */
export const getActiveServices = async () => {
  const { data } = await http.get('/services');
  return data.data || [];
};
export const getCarBrands = async () => {
  const { data } = await http.get('/car-brands');
  return data.data || [];
};
export const getPartsBrands = async () => {
  const { data } = await http.get('/parts-brands');
  return data.data || [];
};
export const getStats = async () => {
  const { data } = await http.get('/stats');
  return data.data || [];
};

/* ── Admin catalogue ─────────────────────────────────────── */
export const getServices     = async ()     => { const {data} = await http.get('/admin/services');          return data.data||[]; };
export const createService   = async d      => { const {data} = await http.post('/admin/services', d);      return data.data; };
export const updateService   = async (id,d) => { const {data} = await http.put(`/admin/services/${id}`,d);  return data.data; };
export const deleteService   = async id     => { await http.delete(`/admin/services/${id}`);                 return {success:true}; };

export const createCarBrand  = async d      => { const {data} = await http.post('/admin/car-brands',d);         return data.data; };
export const updateCarBrand  = async (id,d) => { const {data} = await http.put(`/admin/car-brands/${id}`,d);    return data.data; };
export const deleteCarBrand  = async id     => { await http.delete(`/admin/car-brands/${id}`);                  return {success:true}; };

export const createPartsBrand= async d      => { const {data} = await http.post('/admin/parts-brands',d);       return data.data; };
export const updatePartsBrand= async (id,d) => { const {data} = await http.put(`/admin/parts-brands/${id}`,d);  return data.data; };
export const deletePartsBrand= async id     => { await http.delete(`/admin/parts-brands/${id}`);                return {success:true}; };

export const updateStats     = async stats  => { const {data} = await http.put('/admin/stats',stats);           return data.data; };

/* ── Customer: bookings ──────────────────────────────────── */
export const createBookingApi = async body => {
  const { data } = await http.post('/bookings', body);
  return data.data;
};
export const getMyBookings = async () => {
  const { data } = await http.get('/bookings/my');
  return data.data || [];
};
export const getMyActiveBooking = async () => {
  const { data } = await http.get('/bookings/my/active');
  return data.data || null;
};
export const cancelBookingApi = async id => {
  const { data } = await http.patch(`/bookings/${id}/cancel`);
  return data.data;
};
export const rescheduleBookingApi = async (id, date, timeSlot) => {
  const { data } = await http.patch(`/bookings/${id}/reschedule`, { date, timeSlot });
  return data.data;
};

/* ── Customer: invoices ──────────────────────────────────── */
/**
 * GET /api/v1/bookings/my/{bookingId}/invoice
 * Fetches the invoice for a specific booking that belongs to the logged-in customer.
 * Backend endpoint is in BookingController — secured by JWT, verifies ownership.
 */
export const getMyInvoice = async bookingId => {
  const { data } = await http.get(`/bookings/my/${bookingId}/invoice`);
  return data.data || null;
};

/* ── Admin: bookings ─────────────────────────────────────── */
export const getAllBookings = async status => {
  const params = status ? { status } : {};
  const { data } = await http.get('/bookings', { params });
  return data.data || [];
};
export const confirmBookingByAdmin = async id => {
  const { data } = await http.patch(`/bookings/${id}/confirm`);
  return data.data;
};
export const rejectBookingByAdmin = async (id, reason) => {
  const { data } = await http.patch(`/bookings/${id}/reject`, { reason });
  return data.data;
};
export const updateBookingAdmin = async (id, body) => {
  const { data } = await http.patch(`/bookings/${id}`, body);
  return data.data;
};
export const searchBookings = async q => {
  const { data } = await http.get('/bookings/search', { params: { q } });
  return data.data || [];
};

/* ── Admin: mechanics ────────────────────────────────────── */
export const getMechanics    = async ()     => { const {data} = await http.get('/admin/mechanics');          return data.data||[]; };
export const createMechanic  = async d      => { const {data} = await http.post('/admin/mechanics',d);       return data.data; };
export const updateMechanic  = async (id,d) => { const {data} = await http.put(`/admin/mechanics/${id}`,d);  return data.data; };
export const deleteMechanic  = async id     => { await http.delete(`/admin/mechanics/${id}`);                return {success:true}; };

/* ── Admin: inventory ────────────────────────────────────── */
export const getInventory        = async ()        => { const {data} = await http.get('/admin/inventory');           return data.data||[]; };
export const getLowStockItems    = async ()        => { const {data} = await http.get('/admin/inventory/low-stock'); return data.data||[]; };
export const createInventoryItem = async d         => { const {data} = await http.post('/admin/inventory',d);        return data.data; };
export const updateInventoryItem = async (id,d)    => { const {data} = await http.put(`/admin/inventory/${id}`,d);   return data.data; };
export const deleteInventoryItem = async id        => { await http.delete(`/admin/inventory/${id}`);                 return {success:true}; };
export const adjustStock         = async (id,delta)=> { const {data} = await http.patch(`/admin/inventory/${id}/stock`,{delta}); return data.data; };

/* ── Admin: notifications ────────────────────────────────── */
export const getNotifications         = async ()  => { const {data} = await http.get('/admin/notifications');      return data.data||[]; };
export const getUnreadCount           = async ()  => { const {data} = await http.get('/admin/notifications/unread-count'); return data.data||0; };
export const markAllNotificationsRead = async ()  => { await http.patch('/admin/notifications/read-all'); };
export const dismissNotification      = async id  => { await http.delete(`/admin/notifications/${id}`); };
export const clearAllNotifications    = async ()  => { await http.delete('/admin/notifications'); };


/* ── Admin: invoices ─────────────────────────────────────── */
export const getAllInvoices      = async ()     => { const {data} = await http.get('/admin/invoices');              return data.data||[]; };
export const getInvoiceById     = async id     => { const {data} = await http.get(`/admin/invoices/${id}`);         return data.data; };
export const getInvoiceByBooking= async bid    => { const {data} = await http.get(`/admin/invoices/booking/${bid}`);return data.data; };
export const createInvoice      = async body   => { const {data} = await http.post('/admin/invoices', body);        return data.data; };
export const markInvoicePaid    = async (id,b) => { const {data} = await http.patch(`/admin/invoices/${id}/mark-paid`,b||{}); return data.data; };
export const deleteInvoice      = async id     => { await http.delete(`/admin/invoices/${id}`);                     return {success:true}; };

/* ── SMS simulation ──────────────────────────────────────── */
export const sendBookingSMS = booking => {
  const msg = `AutoElite: Booking ${booking.id} received!\nVehicle: ${booking.vehicleMake} ${booking.vehicleModel} (${booking.regNumber})\nService: ${(booking.services||[]).join(', ')}\nDate: ${booking.date} at ${booking.timeSlot}\nAwaiting confirmation.`;
  console.log(`[SMS → ${booking.phone}]`, msg);
  return Promise.resolve({ success: true, to: booking.phone, message: msg });
};

/* ── useApi hook ─────────────────────────────────────────── */
export function useApi(apiFn, deps = []) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  const run = useCallback(async () => {
    setLoading(true); setError(null);
    try   { setData(await apiFn()); }
    catch (e) { setError(e.response?.data?.message || e.message || 'Failed to load'); }
    finally   { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => { run(); }, [run]);
  return { data, loading, error, refetch: run };
}